import { newTheme } from '@customerio/pluma-components/css';
import customerIoLogo from '@/assets/images/logos/thumbnail-logo-navigation.svg';
import { useAuth } from '@/services/auth';
import { useCurrentWorkspace } from '@/context/current-workspace';
import type { TargetTransitionOptions } from '@/services/router/safe-navigate';
import { WorkspaceDropdown } from '../top-navigation/components/workspace-selector';
import { PlainButton, Image, Box, Icon, Popover, Text, useSideNavState } from '@customerio/pluma-components/react';
import { PlumaSideNav as PlumaSideNavCss } from '@customerio/pluma-components/css/components';
import { AnimatePresence, motion, m } from 'motion/react';
import { useRef, useState } from 'react';
import * as styles from './workspace-selector.css';
import { MODERATE_01_STANDARD_PRODUCTIVE_TRANSITION } from '@/utils/motion';
import { useMountEffect } from '@/hooks/use-mount-effect';

const MotionPlainButton = motion.create(PlainButton);

const SIDE_NAV_PADDING = Number.parseFloat(newTheme.tokensMeta['space-150'].$value);
const EXPANDED_WIDTH = Number.parseFloat(PlumaSideNavCss.values.expandedWidth) - SIDE_NAV_PADDING * 2;
const CONTENT_GAP = Number.parseFloat(newTheme.tokensMeta['space-100'].$value);
const CONTENT_GAP_COLLAPSED = 18;
const CHEVRON_WIDTH =
  Number.parseFloat(newTheme.tokensMeta['space-icon-v2-md'].$value) +
  Number.parseFloat(newTheme.tokensMeta['space-100'].$value);

function useCollapsedWidth() {
  const measureRef = useRef<HTMLDivElement>(null);
  const [collapsedWidth, setCollapsedWidth] = useState<number | null>(null);

  useMountEffect(() => {
    const element = measureRef.current;
    if (element == null) {
      return;
    }

    const observer = new ResizeObserver(([entry]) => {
      if (entry != null) {
        setCollapsedWidth(entry.borderBoxSize[0].inlineSize);
      }
    });
    observer.observe(element);
    return () => observer.disconnect();
  });

  return { measureRef, collapsedWidth };
}

const buttonVariants = {
  expanded: {
    width: EXPANDED_WIDTH,
    transition: MODERATE_01_STANDARD_PRODUCTIVE_TRANSITION,
  },
  collapsed: (collapsedWidth: number | null) => ({
    width: collapsedWidth ?? undefined,
    transition: MODERATE_01_STANDARD_PRODUCTIVE_TRANSITION,
  }),
};

const contentSpacerVariants = {
  expanded: {
    width: CONTENT_GAP,
    transition: MODERATE_01_STANDARD_PRODUCTIVE_TRANSITION,
  },
  collapsed: {
    width: CONTENT_GAP_COLLAPSED,
    transition: MODERATE_01_STANDARD_PRODUCTIVE_TRANSITION,
  },
};

const chevronVariants = {
  initial: {
    opacity: 0,
    width: 0,
  },
  animate: {
    opacity: 1,
    width: CHEVRON_WIDTH,
    // Typically we'd use enter/exit easings for appearing/disappearing
    // content, but we want this transition to be in sync with the button itself
    transition: MODERATE_01_STANDARD_PRODUCTIVE_TRANSITION,
  },
  exit: {
    opacity: 0,
    width: 0,
    transition: MODERATE_01_STANDARD_PRODUCTIVE_TRANSITION,
  },
};

export function WorkspaceSelector(props: {
  onLinkClick: (event: React.MouseEvent<HTMLAnchorElement>, options?: TargetTransitionOptions) => void;
}) {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  const currentWorkspace = useCurrentWorkspace();
  const { measureRef, collapsedWidth } = useCollapsedWidth();

  const sideNavState = useSideNavState();
  const isSideNavCollapsed = sideNavState?.isCollapsed ?? false;

  const onWorkspaceLinkClick = async (
    event: React.MouseEvent<HTMLAnchorElement>,
    options?: TargetTransitionOptions,
  ) => {
    props.onLinkClick(event, options);
    setIsPopoverOpen(false);
  };

  const auth = useAuth();
  const logoForNav =
    auth.account?.logo_url !== '' && auth.account?.logo_url != null ? auth.account?.logo_url : customerIoLogo;

  const variantName = isSideNavCollapsed ? 'collapsed' : 'expanded';

  return (
    <>
      <Popover isOpen={isPopoverOpen} onOpenChange={setIsPopoverOpen} placement="bottom-start" withArrow={false}>
        {(context) => (
          <>
            <MotionPlainButton
              className={styles.workspaceSelectorButton}
              aria-label={`${currentWorkspace?.name} workspace`}
              aria-description="Click to open workspace selector."
              ref={context.reference.setReference}
              {...context.reference.getReferenceProps()}
              initial={false}
              animate={variantName}
              custom={collapsedWidth}
              variants={buttonVariants}
            >
              <m.div initial={false} className={styles.workspaceSelectorButtonContent}>
                <Box className={styles.workspaceSelectorButtonImageContainer}>
                  <Image src={logoForNav} alt="" width={24} height={24} flexShrink="0" />
                </Box>

                <m.div
                  className={styles.workspaceSelectorButtonContentSpacer}
                  initial={false}
                  variants={contentSpacerVariants}
                />

                <Text text="product-ps-active" flexGrow="1" truncate={true}>
                  {currentWorkspace?.name}
                </Text>
              </m.div>

              <AnimatePresence initial={false}>
                {isSideNavCollapsed ? null : (
                  <m.div
                    className={styles.workspaceSelectorButtonChevronContainer}
                    variants={chevronVariants}
                    initial="initial"
                    animate="animate"
                    exit="exit"
                  >
                    <Icon name="chevron-toggle" flexShrink="0" />
                  </m.div>
                )}
              </AnimatePresence>
            </MotionPlainButton>
            <WorkspaceDropdown onWorkspaceLinkClick={onWorkspaceLinkClick} />
          </>
        )}
      </Popover>

      {/* Hidden clone in permanent collapsed layout to measure auto width */}
      <div
        ref={measureRef}
        aria-hidden="true"
        className={`${styles.workspaceSelectorButton} ${styles.workspaceSelectorMeasure}`}
      >
        <div className={styles.workspaceSelectorButtonContent}>
          <Box className={styles.workspaceSelectorButtonImageContainer}>
            <Image src={logoForNav} alt="" width={24} height={24} flexShrink="0" />
          </Box>
          <div className={styles.workspaceSelectorButtonContentSpacer} style={{ width: CONTENT_GAP_COLLAPSED }} />
          <Text text="product-ps-active" flexGrow="1" truncate={true}>
            {currentWorkspace?.name}
          </Text>
        </div>
      </div>
    </>
  );
}
